import { useMemo } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { useBloomreachCookieGetter } from '../../hooks/useBloomreachCookie';
import { isServer } from '../../utils/utils';
import { useBffGet } from '../bff';
import { BffQueryKey } from '../model/BffQueryKey';
import type { HttpParams } from '../../utils/url-utils';
import type { BFFHttpError } from '../model/BFFHttpError';
import type { BloomreachRecommendationsResponse } from './model/BloomreachRecommendationsResponse';
import type { RecommendationParams } from './model/RecommendationParams';

export const useRecommendation = ({
    widgetType,
    widgetId,
    productId,
    categoryId,
    productTypeFilter,
    brandCategoryId,
    collectionId,
    maxResults,
    userId,
}: RecommendationParams): UseQueryResult<
    BloomreachRecommendationsResponse,
    BFFHttpError | null
> => {
    const getBrUID2Cookie = useBloomreachCookieGetter(true);
    const { keys, params } = useMemo<{ keys: string[]; params: HttpParams }>(() => {
        const baseParams = [
            { widgetType },
            { widgetId },
            ...(productTypeFilter ? [{ productTypeFilter }] : []),
            ...(productId ? [{ productId }] : []),
            ...(categoryId ? [{ categoryId }] : []),
            ...(brandCategoryId ? [{ brandCategoryId }] : []),
            ...(collectionId ? [{ collectionId }] : []),
            ...(userId ? [{ userId }] : []),
            { maxResults },
        ] as HttpParams;

        const baseKeys = [
            BffQueryKey.BLOOMREACH_RECOMMENDATION,
            widgetType,
            widgetId,
            ...(productTypeFilter ? [productTypeFilter] : []),
            ...(productId ? [productId] : []),
            ...(categoryId ? [categoryId] : []),
            ...(brandCategoryId ? [brandCategoryId] : []),
            ...(collectionId ? [collectionId] : []),
            ...(userId ? [userId] : []),
        ];
        return { params: baseParams, keys: baseKeys };
    }, [
        widgetType,
        widgetId,
        productTypeFilter,
        maxResults,
        productId,
        categoryId,
        brandCategoryId,
        collectionId,
        userId,
    ]);

    return useBffGet<BloomreachRecommendationsResponse>(
        'bloomreach/recommendations',
        () => {
            const brUID2 = getBrUID2Cookie();
            if (brUID2) {
                return [...params, { brUID2 }];
            }
            return params;
        },
        keys,
        {
            enabled: !!widgetId && !isServer,
        },
    );
};
